import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { IUserInvitation } from '@shared/interfaces/user-invitation.interface';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserInvitationService {
  private readonly http = inject(HttpClient);

  public getSupportUserInvitation(id: string): Observable<IUserInvitation> {
    return this.http.get<IUserInvitation>(`invitations/support-user/${id}`);
  }

  public getSupportUserInvitations(params?: HttpParams): Observable<IApiResponse<IUserInvitation[]>> {
    return this.http.get<IApiResponse<IUserInvitation[]>>(`invitations/support-user`, { params });
  }

  public sendSupportUserInvitation(email: string): Observable<IUserInvitation> {
    return this.http.post<IUserInvitation>('invitations/support-user', { email });
  }

  public revokeSupportUserInvitation(id: string): Observable<IUserInvitation> {
    return this.http.delete<IUserInvitation>(`invitations/support-user/${id}`);
  }

  public getCampaignUserInvitation(id: string): Observable<IUserInvitation> {
    return this.http.get<IUserInvitation>(`invitations/campaign-user/${id}`);
  }

  public getCampaignUserInvitations(params?: HttpParams): Observable<IApiResponse<IUserInvitation[]>> {
    return this.http.get<IApiResponse<IUserInvitation[]>>(`invitations/campaign-user`, { params });
  }

  public sendCampaignUserInvitation(email: string, clientName: string): Observable<IUserInvitation> {
    return this.http.post<IUserInvitation>('invitations/campaign-user', { clientName, email });
  }

  public revokeCampaignUserInvitation(id: string): Observable<IUserInvitation> {
    return this.http.delete<IUserInvitation>(`invitations/campaign-user/${id}`);
  }

  public getCampaignSubUserInvitation(id: string): Observable<IUserInvitation> {
    return this.http.get<IUserInvitation>(`invitations/campaign-sub-user/${id}`);
  }

  public getCampaignSubUserInvitations(params?: HttpParams): Observable<IApiResponse<IUserInvitation[]>> {
    return this.http.get<IApiResponse<IUserInvitation[]>>(`invitations/campaign-sub-user`, { params });
  }

  public sendCampaignSubUserInvitation(
    email: string,
    message: string,
    roleId?: string,
    teamId?: string | null,
  ): Observable<IUserInvitation> {
    return this.http.post<IUserInvitation>('invitations/campaign-sub-user', {
      email,
      message,
      roleId,
      teamId,
    });
  }

  /** Adjusts the target role/team of a still-pending (not yet accepted) campaign-sub-user
   * invitation in place - same invitation id, no revoke/resend, no email re-sent. Only valid
   * while the invitation has no `createdUserId` yet (backend rejects it with
   * INVITATION_ALREADY_ACCEPTED otherwise) - i.e. status 'pending', never 'joined' or 'expired'. */
  public updatePendingCampaignSubUserInvitation(
    id: string,
    roleId: string,
    teamId: string | null,
  ): Observable<IUserInvitation> {
    return this.http.put<IUserInvitation>(`invitations/campaign-sub-user/${id}`, {
      roleId,
      teamId,
    });
  }

  public revokeCampaignSubUserInvitation(id: string): Observable<IUserInvitation> {
    return this.http.delete<IUserInvitation>(`invitations/campaign-sub-user/${id}`);
  }

  public getCreatorInvitation(id: string): Observable<IUserInvitation> {
    return this.http.get<IUserInvitation>(`invitations/creator/${id}`);
  }

  public getCreatorInvitations(params?: HttpParams): Observable<IApiResponse<IUserInvitation[]>> {
    return this.http.get<IApiResponse<IUserInvitation[]>>(`invitations/creator`, { params });
  }

  public sendCreatorInvitation(email: string, message: string, creatorId: string): Observable<IUserInvitation> {
    return this.http.post<IUserInvitation>('invitations/creator', { creatorId, email, message });
  }

  public revokeCreatorInvitation(id: string): Observable<IUserInvitation> {
    return this.http.delete<IUserInvitation>(`invitations/creator/${id}`);
  }

  public acceptInvitation(invitationId: string): Observable<string> {
    return this.http.post<string>(`invitations/accept/${invitationId}`, {});
  }
}
